/**
 * LegacyBrowserBanner — visible only on Safari 12 / iOS 12 (and other
 * engines that lack ES2018 lookbehind regex). On these browsers we fall
 * back to a plain-text renderer for markdown surfaces, so the user
 * sees raw `**bold**` instead of bold. The banner explains why and
 * lets them dismiss it.
 *
 * Renders nothing on modern browsers — zero footprint in the happy path.
 *
 * English only — UI text policy.
 */
import { Info, X } from 'lucide-react';
import * as React from 'react';

import { isLegacyRegexBrowser } from '../../lib/legacy-browser';
import { cn } from '../../lib/utils';

const DISMISS_STORAGE_KEY = 'chaos-legacy-browser-banner-dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(value: boolean): void {
  try {
    if (value) localStorage.setItem(DISMISS_STORAGE_KEY, '1');
    else localStorage.removeItem(DISMISS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function LegacyBrowserBanner(): React.JSX.Element | null {
  const [dismissed, setDismissed] = React.useState<boolean>(() => readDismissed());

  // Bail out before touching state on modern browsers — keeps the
  // component tree clean.
  if (!isLegacyRegexBrowser()) return null;
  if (dismissed) return null;

  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-2 border-b border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground',
      )}
    >
      <Info className="mt-[1px] h-3.5 w-3.5 shrink-0" aria-hidden />
      <p className="flex-1 leading-snug">
        Your browser is older than what CHAOS targets. Rich formatting
        for messages and notes is disabled — you will see the raw
        Markdown source instead. Update Safari (iOS 16+ recommended) for
        the full experience.
      </p>
      <button
        type="button"
        onClick={() => {
          writeDismissed(true);
          setDismissed(true);
        }}
        aria-label="Dismiss"
        className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}
